import PlayerError from '@src/errors/PlayerError';
import type {
  VideoId,
  VideoJsOnErrorCallback,
  VideoObj,
} from '@src/types/player';

import logger from './logger';

type GetAndParseVcpInfoPayload = {
  onError?: VideoJsOnErrorCallback;
  mediaServerURL: string;
  videoIdOrObject?: VideoId | VideoObj;
};

export const getAndParseVcpInfo = async (
  payload: GetAndParseVcpInfoPayload
) => {
  const { videoIdOrObject, mediaServerURL, onError = logger.error } = payload;

  if (!videoIdOrObject) return;

  const info =
    typeof videoIdOrObject === 'string'
      ? await getVcpInfo({
          videoId: videoIdOrObject,
          onError,
          mediaServerURL,
        })
      : videoIdOrObject;

  return parseVcpInfo(info, mediaServerURL);
};

export const getVcpInfo = async (payload: {
  videoId: string;
  mediaServerURL: string;
  onError?: GetAndParseVcpInfoPayload['onError'];
}) => {
  const { videoId, mediaServerURL, onError = logger.error } = payload;
  const infoURL = `${encodeURI(mediaServerURL)}/${encodeURIComponent(
    videoId
  )}/info.json`;

  let info;

  try {
    const fetchResp = await fetch(infoURL, {
      method: 'GET',
      credentials: 'include',
    });
    info = await fetchResp.json();
  } catch (error) {
    const _error = new PlayerError(`Fail to fetch info json: ${videoId}`, {
      cause: error,
    });
    onError(_error, null);
  }

  return info;
};

export const parseVcpInfo = (
  info: {
    img: string;
    img_s3: string;
    url: string;
    url_s3: string;
    title: string;
    description: string;
  },
  mediaServerURL: string
) => {
  let poster, src;

  const { img, img_s3, url, url_s3, title, description } = info ?? {};

  if (img) {
    poster = encodeURI(img);
  } else if (img_s3 && mediaServerURL) {
    poster = `${mediaServerURL}/${encodeURI(img_s3)}`;
  } else {
    poster = undefined;
  }
  if (url) {
    src = encodeURI(url);
  } else if (url_s3 && mediaServerURL) {
    src = `${mediaServerURL}/${encodeURI(url_s3)}`;
  } else {
    src = undefined;
  }

  return {
    title: title || undefined,
    description: description || undefined,
    poster,
    src,
  };
};
